<template>
  <div class="bind-phone-wrapper">
    <div class="form-item area-select">
      <div class="label">{{ t('lang266') }}</div>
      <a-dropdown position="br">
        <div class="cur-area">{{ areaName }} <icon-right /></div>
        <template #content>
          <a-doption
            @click="onSelect(item.value)"
            v-for="item in countryList"
            :value="item.value"
            :key="item.value"
            >{{ item.name }}</a-doption
          >
        </template>
      </a-dropdown>
    </div>
    <div class="form-item phone-input">
      <div class="area-code">+{{ areaPhoneCode }}</div>
      <input v-model="phone" maxlength="20" type="phone" :placeholder="t('lang267')" />
    </div>

    <div class="form-item">
      <input v-model="verifyCode" maxlength="10" type="phone" :placeholder="t('lang268')" />
      <button class="send-btn" type="button" :disabled="timer.running || !phone" @click="onSend">
        {{ timer.running ? t('lang024', [timer.current]) : t('lang023') }}
      </button>
    </div>
    <div class="footer">
      <a-button
        :loading="loading"
        :disabled="!phone || !areaPhoneCode"
        type="primary"
        @click="onOk"
        >{{ t('lang244') }}</a-button
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { countryList, codeMap, countryNameMap, CountryShortName } from './phoneData';

import useCountdown from '@/compositions/useCountdown';
import {
  useLiveAgentUnionOssSendVerifyCode,
  useLiveAgentUnionOssSetUidHasPhone,
} from '@/api/yapi-req';
import { SEND_VERIFY_CODE_SCENE, SEND_VERIFY_CODE_TYPE } from '@/constants';
import { useAppStore } from '@/store/app';
import { useThrottleFn } from '@vueuse/core';
import { Message } from '@arco-design/web-vue';
import { STATUS_CODE } from './constants';
import useTrack from '@/compositions/useTrack';

const { t } = useI18n();
const SEND_CODE_STORAGE_KEY = 'BIND_PHONE_CODE_LAST_SEND_TIME';
const SEND_CODE_COUNTDOWN = 60;
const timer = useCountdown(SEND_CODE_STORAGE_KEY, SEND_CODE_COUNTDOWN);
const areaCode = ref<CountryShortName>('CN');
const phone = ref('');
const verifyCode = ref('');
const appStore = useAppStore();
const { report } = useTrack('bind_phone');

const { run: sendVerifyCode } = useLiveAgentUnionOssSendVerifyCode({
  manual: true,
  onSuccess: () => {
    timer.start();
    report('sendVerifyCodeSuccess');
  },
  onError: () => {
    Message.error(t('lang572'));
    report('sendVerifyCodeError');
  },
});
const { run: postBindPhone, loading } = useLiveAgentUnionOssSetUidHasPhone({
  manual: true,
  onSuccess: () => {
    appStore.setPhoneInfo({
      prefix: areaPhoneCode.value,
      phone: phone.value,
    });
    appStore.setPhoneAuthSuccess();
    report('bindPhoneSuccess');
    close();
  },
  onError: (res) => {
    report('bindPhoneError');
    if (res.code === STATUS_CODE.CHECK_CODE_FAILED) {
      Message.error(t('lang330'));
      return;
    }
    Message.error(t('lang573'));
  },
});
const emit = defineEmits(['click']);

const areaName = computed(() => {
  return countryNameMap[areaCode.value];
});

const areaPhoneCode = computed(() => {
  return codeMap[areaCode.value];
});

const phoneNum = computed(() => `${areaPhoneCode.value}${phone.value}`);

const onSend = useThrottleFn(() => {
  if (timer.running) return;
  sendVerifyCode({
    type: SEND_VERIFY_CODE_TYPE.PHONE_MSG,
    scene: SEND_VERIFY_CODE_SCENE.BIND_PHONE,
    phone: phoneNum.value,
    bigo_id: '',
  });
});

const onSelect = (val: string) => {
  areaCode.value = val as CountryShortName;
};

const onOk = async () => {
  await postBindPhone({
    phone: phoneNum.value,
    code: verifyCode.value,
  });
};

const close = () => emit('click');

onMounted(() => {
  report('displayBindModal');
});
</script>

<style lang="less" scoped>
.bind-phone-wrapper {
  box-sizing: border-box;

  .form-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding: 7px 16px;
    height: 48px;
    border-radius: 8px;
    background: var(--color-fill-1, #f7f8fa);
    padding: 7px 16px;

    /* 14/CN-Regular */
    font-family: PingFang SC;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;

    input {
      line-height: 22px;
      border: none;
      background: transparent;
      flex: 1;

      color: var(--color-text-1, #1d2129);
      font-family: PingFang SC;
      font-weight: 700;

      &:focus {
        border: none;
        background: transparent;
        outline: none;
      }
      &::placeholder {
        color: var(--color-text-3, #86909c);
        font-weight: 400;
      }
    }

    .label {
      color: var(--color-text-3, #86909c);
    }
  }
  .area-select {
    color: var(--color-text-1, #1d2129);

    /* 14/CN-Regular */
    font-family: PingFang SC;

    .cur-area {
      cursor: pointer;
      display: flex;
      align-items: center;
      height: 100%;
    }
  }

  .phone-input {
    justify-content: flex-start;
    .area-code {
      flex-shrink: 0;
      margin-right: 8px;

      color: var(--color-text-1, #1d2129);

      /* 14/EN-Bold */
      font-family: PingFang SC;
      font-size: 14px;
      font-style: normal;
      font-weight: 700;
      line-height: 22px; /* 157.143% */
    }
  }
  .send-btn {
    padding: 0 3px;
    font-weight: 700;
    color: var(--color-text-1);
    background: none;
    border: none;
    cursor: pointer;
    height: 100%;
    &[disabled] {
      color: var(--color-text-3, #86909c);
    }
  }

  .footer {
    text-align: center;
    padding: 32px;
  }
}
</style>
